import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { 
  Calculator, 
  Truck, 
  Clock, 
  BadgePercent,
  ArrowRight,
  CheckCircle2,
  Mail,
  Phone,
  MapPin,
  ChevronRight,
  Menu,
  X
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LandingPage = () => {
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success("Successfully subscribed to our newsletter!");
      setEmail("");
    } catch (error) {
      toast.error("Failed to subscribe. Please try again.");
    } finally {
      setSubscribing(false);
    }
  };

  const features = [
    {
      icon: <Calculator className="w-6 h-6" />,
      title: "Instant Pricing",
      description: "Transparent quotes in seconds. No waiting for estimates or hidden costs."
    },
    {
      icon: <BadgePercent className="w-6 h-6" />,
      title: "Hotel Grade Quality",
      description: "The same premium finish used in leading hospitality properties."
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Swift Turnaround",
      description: "Efficient production with most orders ready within 5 working days."
    },
    {
      icon: <Truck className="w-6 h-6" />,
      title: "Nationwide Delivery",
      description: "Professional delivery service across the UK or collection available."
    }
  ];

  const steps = [
    { num: "01", title: "Enter Dimensions", desc: "Input your sill or shelf measurements" },
    { num: "02", title: "Choose Options", desc: "Select edge finish and additions" },
    { num: "03", title: "Get Instant Price", desc: "See your quote immediately" },
    { num: "04", title: "Place Order", desc: "Submit your order online" }
  ];

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      {/* Navigation */}
      <nav className="nav-wrapper" data-testid="navigation">
        <div className="container-padding py-4 flex items-center justify-between max-w-7xl mx-auto">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#0F3D2E] flex items-center justify-center">
              <span className="text-white font-semibold text-lg">S</span>
            </div>
            <span className="font-semibold text-lg tracking-tight">Stone Sills Direct</span>
          </Link>
          
          <div className="hidden md:flex items-center gap-8">
            <Link to="/calculator" className="nav-link">Calculator</Link>
            <Link to="/shop" className="nav-link">Shop</Link>
            <Link to="/visualiser" className="nav-link">Visualiser</Link>
            <Link to="/orders" className="nav-link">My Orders</Link>
            <Link to="/installation" className="nav-link">Installation</Link>
            <a href="#contact" className="nav-link">Contact</a>
          </div>
          
          <div className="flex items-center gap-3">
            <Link to="/calculator" className="hidden sm:block">
              <Button className="btn-primary" data-testid="get-quote-btn">
                Get Quote <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <button
              className="md:hidden p-2 text-[#1C1917]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden overflow-hidden bg-white border-t border-[#E7E5E4]"
              data-testid="mobile-menu"
            >
              <div className="container-padding py-4 flex flex-col gap-1">
                {[
                  { to: "/calculator", label: "Calculator" },
                  { to: "/shop", label: "Shop" },
                  { to: "/visualiser", label: "Visualiser" },
                  { to: "/orders", label: "My Orders" },
                  { to: "/installation", label: "Installation" },
                ].map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="py-3 px-4 text-[#1C1917] hover:bg-[#F5F5F4] rounded-lg font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {item.label}
                  </Link>
                ))}
                <a
                  href="#contact"
                  className="py-3 px-4 text-[#1C1917] hover:bg-[#F5F5F4] rounded-lg font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Contact
                </a>
                <Link to="/calculator" className="mt-2" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="btn-primary w-full" data-testid="mobile-get-quote-btn">
                    Get Quote <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div 
          className="hero-bg"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1736506159814-25bcd056da22?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHw0fHx3aGl0ZSUyMG1hcmJsZSUyMHRleHR1cmUlMjBmdWxsJTIwZnJhbWV8ZW58MHx8fHwxNzczNjg2MjM0fDA&ixlib=rb-4.1.0&q=85')" }}
        />
        
        <div className="container-padding max-w-7xl mx-auto relative z-10 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div 
              className="lg:col-span-7"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="section-caption text-[#C27C2E] mb-4 block">
                Trusted by Leading Hotel Groups
              </span>
              <h1 className="text-4xl md:text-6xl font-medium tracking-tight leading-tight mb-6">
                Luxury Quartz
                <br />
                <span className="text-[#0F3D2E]">Window Sills & Shelving</span>
              </h1>
              <p className="text-lg md:text-xl text-[#57534E] leading-relaxed mb-8 max-w-xl">
                Premium stone craftsmanship for discerning clients. From boutique hotels 
                to luxury residences, we deliver exceptional quality with precision and care.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/calculator">
                  <Button className="btn-primary w-full sm:w-auto" data-testid="hero-calculator-btn">
                    Start Calculator <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <a href="#how-it-works">
                  <Button variant="outline" className="btn-secondary w-full sm:w-auto">
                    How It Works
                  </Button>
                </a>
              </div>

              <div className="flex items-center gap-6 mt-8 pt-8 border-t border-[#E7E5E4]">
                <div className="flex items-center gap-2 text-sm text-[#57534E]">
                  <CheckCircle2 className="w-5 h-5 text-[#0F3D2E]" />
                  <span>Hotel Projects</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-[#57534E]">
                  <CheckCircle2 className="w-5 h-5 text-[#0F3D2E]" />
                  <span>Premium Quality</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-[#57534E]">
                  <CheckCircle2 className="w-5 h-5 text-[#0F3D2E]" />
                  <span>Nationwide Delivery</span>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="lg:col-span-5"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="gallery-image aspect-[4/5] shadow-[0_24px_48px_-12px_rgba(28,25,23,0.18)]">
                <img 
                  src="https://images.unsplash.com/photo-1758448755927-e5c5ae14790c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjBraXRjaGVuJTIwc3RvbmUlMjBjb3VudGVydG9wfGVufDB8fHx8MTc3MzY4NjIzNXww&ixlib=rb-4.1.0&q=85"
                  alt="Luxury quartz installation"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="features-section">
        <div className="container-padding max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="section-caption text-[#C27C2E] mb-4 block">Why Choose Us</span>
            <h2 className="section-header">The Premium Choice for<br />Luxury Stone</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="stone-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-12 h-12 bg-[#0F3D2E] flex items-center justify-center text-white mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-medium mb-3">{feature.title}</h3>
                <p className="text-[#57534E] leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 md:py-32" data-testid="how-it-works">
        <div className="container-padding max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <span className="section-caption text-[#C27C2E] mb-4 block">Effortless Ordering</span>
              <h2 className="section-header mb-6">How It Works</h2>
              <p className="text-lg text-[#57534E] leading-relaxed mb-8">
                Our instant calculator streamlines your ordering process. 
                Whether you're fitting out a single room or an entire hotel, 
                get precise pricing in moments.
              </p>
              
              <Link to="/calculator">
                <Button className="btn-primary" data-testid="try-calculator-btn">
                  Try Calculator <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {steps.map((step, index) => (
                  <motion.div
                    key={step.num}
                    className="stone-card"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <span className="text-4xl font-medium text-[#E7E5E4] mb-4 block">{step.num}</span>
                    <h3 className="text-lg font-medium mb-2">{step.title}</h3>
                    <p className="text-sm text-[#57534E]">{step.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-20 md:py-32 bg-white" data-testid="gallery-section">
        <div className="container-padding max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="section-caption text-[#C27C2E] mb-4 block">Our Portfolio</span>
            <h2 className="section-header">Trusted by Industry Leaders</h2>
            <p className="text-[#57534E] mt-4 max-w-2xl mx-auto">
              From nationwide hotel chains to luxury private residences, our craftsmanship 
              sets the standard for premium stone installations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div 
              className="gallery-image aspect-square md:row-span-2 md:aspect-auto"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://images.unsplash.com/photo-1762529716215-afd7400d7bd4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsdXh1cnklMjBraXRjaGVuJTIwY291bnRlcnRvcCUyMHF1YXJ0enxlbnwwfHx8fDE3NzM2ODk1MTd8MA&ixlib=rb-4.1.0&q=85"
                alt="Luxury kitchen with stone countertop"
                className="w-full h-full object-cover"
              />
            </motion.div>
            <motion.div 
              className="gallery-image aspect-video"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://images.unsplash.com/photo-1714648775497-9f54f75eb3ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBob3RlbCUyMGJhdGhyb29tJTIwbWFyYmxlJTIwc3RvbmV8ZW58MHx8fHwxNzczNjg5NDk0fDA&ixlib=rb-4.1.0&q=85"
                alt="Luxury hotel bathroom"
                className="w-full h-full object-cover"
              />
            </motion.div>
            <motion.div 
              className="gallery-image aspect-video"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://images.unsplash.com/photo-1636628751643-2a8c9fd001d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBsdXh1cnklMjBraXRjaGVuJTIwY291bnRlcnRvcCUyMHF1YXJ0enxlbnwwfHx8fDE3NzM2ODk1MTd8MA&ixlib=rb-4.1.0&q=85"
                alt="Kitchen with marble countertops"
                className="w-full h-full object-cover"
              />
            </motion.div>
            <motion.div 
              className="gallery-image aspect-video md:col-span-2"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://images.unsplash.com/photo-1768346564825-6f90c0b89e2e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBob3RlbCUyMGxvYmJ5JTIwaW50ZXJpb3IlMjBkZXNpZ258ZW58MHx8fHwxNzczNjg5NTAxfDA&ixlib=rb-4.1.0&q=85"
                alt="Modern hotel lobby"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Materials Info */}
      <section className="py-20 md:py-32" data-testid="materials-section">
        <div className="container-padding max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="section-caption text-[#C27C2E] mb-4 block">Exceptional Quality</span>
              <h2 className="section-header mb-6">Luxury Quartz</h2>
              <p className="text-lg text-[#57534E] leading-relaxed mb-6">
                We exclusively use premium 20mm engineered quartz — the material of choice 
                for luxury hotels, high-end residences, and commercial properties worldwide. 
                Combining natural beauty with superior durability.
              </p>
              
              <div className="space-y-4">
                {[
                  "Impeccable scratch and stain resistance",
                  "Non-porous, hygienic surface",
                  "Effortless maintenance",
                  "Extensive colour palette",
                  "10+ year manufacturer warranty"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#0F3D2E] flex-shrink-0" />
                    <span className="text-[#57534E]">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="stone-card p-0 overflow-hidden">
              <div className="p-8 bg-[#0F3D2E] text-white">
                <h3 className="text-2xl font-medium mb-2">Hospitality Expertise</h3>
                <p className="text-white/80">Supplying major hotel groups across the UK</p>
              </div>
              <div className="p-8">
                <p className="text-[#57534E] mb-6">
                  Our experience with hotel chains means we understand 
                  the exacting standards required for luxury hospitality. Every piece is 
                  precision-cut and quality assured.
                </p>
                <Link to="/calculator" className="block">
                  <Button className="btn-primary w-full" data-testid="check-pricing-btn">
                    Get Your Quote
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-20 md:py-32 bg-[#0F3D2E]" data-testid="newsletter-section">
        <div className="container-padding max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-medium text-white mb-4">
            Join Our Trade List
          </h2>
          <p className="text-white/70 mb-8">
            Receive exclusive trade pricing, new material announcements, 
            and priority access to limited stock.
          </p>
          
          <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-12 bg-white/10 border-white/20 text-white placeholder:text-white/50 flex-1"
              data-testid="newsletter-email"
            />
            <Button 
              type="submit" 
              className="h-12 bg-[#C27C2E] hover:bg-[#A66825] text-white px-8"
              disabled={subscribing}
              data-testid="newsletter-submit"
            >
              {subscribing ? "Subscribing..." : "Subscribe"}
            </Button>
          </form>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 md:py-32 bg-white" data-testid="contact-section">
        <div className="container-padding max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <span className="section-caption text-[#C27C2E] mb-4 block">Get In Touch</span>
              <h2 className="section-header mb-6">Let's Discuss Your Project</h2>
              <p className="text-lg text-[#57534E] leading-relaxed mb-8">
                Whether you're a hotel group planning a refurbishment, a contractor with 
                a residential project, or a homeowner seeking the finest finishes — 
                we're here to help.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F5F5F4] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#0F3D2E]" />
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Email</h4>
                    <a href="mailto:worktops@setinstonedidcot.co.uk" className="text-[#57534E] hover:text-[#0F3D2E]">
                      worktops@setinstonedidcot.co.uk
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F5F5F4] flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#0F3D2E]" />
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Phone</h4>
                    <span className="text-[#57534E]">Contact via email for fastest response</span>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F5F5F4] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#0F3D2E]" />
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Location</h4>
                    <span className="text-[#57534E]">Didcot, Oxfordshire</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="stone-card">
              <h3 className="text-2xl font-medium mb-6">Request a Quote</h3>
              <p className="text-[#57534E] mb-6">
                For the fastest service, use our instant calculator. 
                It provides accurate pricing immediately.
              </p>
              <Link to="/calculator">
                <Button className="btn-primary w-full" data-testid="contact-quote-btn">
                  Go to Calculator <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="container-padding max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-white flex items-center justify-center">
                  <span className="text-[#0F3D2E] font-semibold text-lg">S</span>
                </div>
                <span className="text-white font-semibold text-lg">Stone Sills Direct</span>
              </div>
              <p className="text-[#A8A29E] max-w-sm">
                Premium luxury quartz and granite window sills, shelving and accessories. Trusted supplier to 
                leading hotel groups and discerning homeowners across the UK.
              </p>
            </div>
            
            <div>
              <h4>Quick Links</h4>
              <ul className="space-y-3">
                <li><Link to="/calculator">Calculator</Link></li>
                <li><Link to="/shop">Shop</Link></li>
                <li><Link to="/visualiser">Visualiser</Link></li>
                <li><Link to="/orders">My Orders</Link></li>
                <li><Link to="/installation">Installation Guide</Link></li>
                <li><a href="#contact">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h4>Services</h4>
              <ul className="space-y-3">
                <li><span>Window Sills</span></li>
                <li><span>Stone Shelves</span></li>
                <li><span>Hotel Projects</span></li>
                <li><span>Nationwide Delivery</span></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-[#A8A29E]">
              © 2026 Stone Sills Direct. All rights reserved.
            </p>
            <Link to="/admin/login" className="text-sm text-[#A8A29E] hover:text-white">
              Admin
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
